/**
 * TimerFix - Comprehensive fix for timer issues
 */

import { preciseTimer } from './PreciseTimer';
import { timerDebugger } from './TimerDebugger';

// ============================================================================
// TYPES AND INTERFACES
// ============================================================================

export interface TimerFixState {
  isFixed: boolean;
  issues: string[];
  fixes: string[];
  lastCheck: number;
  healthScore: number;
}

export interface HealthCheckResult {
  isHealthy: boolean;
  issues: string[];
  score: number;
}

export interface FixResult {
  success: boolean;
  issuesFixed: string[];
  fixesApplied: string[];
  timestamp: number;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const TIMER_FIX_CONSTANTS = {
  HEALTH: {
    TIME_DRIFT_THRESHOLD: 5, // seconds
    MIN_HEALTH_SCORE: 80, // percentage
    MAX_PROGRESS: 100,
    MIN_PROGRESS: 0
  },
  LOGGING: {
    EMOJI: {
      APPLY: '🔧',
      SUCCESS: '✅',
      WARNING: '⚠️',
      RESET: '🔄',
      EMERGENCY: '🚨',
      HEALTH: '🏥'
    },
    PREFIX: 'TimerFix'
  },
  FIX_TYPES: {
    STATE_MANAGEMENT: 'state_management',
    PAUSE_RESUME: 'pause_resume',
    INTERVAL_HANDLING: 'interval_handling',
    SYNCHRONIZATION: 'synchronization'
  }
} as const;

// ============================================================================
// TIMER FIX CLASS
// ============================================================================

/**
 * Comprehensive timer issue detection and automatic fixing system
 * Monitors timer health and applies fixes to resolve common issues
 */
class TimerFix {
  private state: TimerFixState = {
    isFixed: false,
    issues: [],
    fixes: [],
    lastCheck: 0,
    healthScore: 100
  };

  // ============================================================================
  // PUBLIC API METHODS
  // ============================================================================

  /**
   * Apply comprehensive timer fixes for all detected issues
   * @returns FixResult object with details of applied fixes
   */
  applyFixes(): FixResult {
    this.log('Applying comprehensive fixes...', { action: 'apply_fixes' });
    
    const startTime = Date.now();
    const issuesFixed: string[] = [];
    const fixesApplied: string[] = [];

    try {
      // Apply fixes in order of priority
      const stateManagementResult = this.fixStateManagement();
      if (stateManagementResult.fixesApplied.length > 0) {
        fixesApplied.push(...stateManagementResult.fixesApplied);
        issuesFixed.push(...stateManagementResult.issuesFixed);
      }

      const pauseResumeResult = this.fixPauseResumeLogic();
      if (pauseResumeResult.fixesApplied.length > 0) {
        fixesApplied.push(...pauseResumeResult.fixesApplied);
        issuesFixed.push(...pauseResumeResult.issuesFixed);
      }

      const intervalResult = this.fixIntervalHandling();
      if (intervalResult.fixesApplied.length > 0) {
        fixesApplied.push(...intervalResult.fixesApplied);
        issuesFixed.push(...intervalResult.issuesFixed);
      }

      const syncResult = this.fixSynchronization();
      if (syncResult.fixesApplied.length > 0) {
        fixesApplied.push(...syncResult.fixesApplied);
        issuesFixed.push(...syncResult.issuesFixed);
      }

      // Update state
      this.state.isFixed = true;
      this.state.lastCheck = Date.now();
      this.state.issues = [...this.state.issues, ...issuesFixed];
      this.state.fixes = [...this.state.fixes, ...fixesApplied];

      const result: FixResult = {
        success: true,
        issuesFixed,
        fixesApplied,
        timestamp: Date.now()
      };

      this.log('All fixes applied successfully', result);
      return result;

    } catch (error) {
      this.log('Error applying fixes', { error, action: 'apply_fixes_error' });
      
      return {
        success: false,
        issuesFixed: [],
        fixesApplied: [],
        timestamp: Date.now()
      };
    }
  }

  /**
   * Check if timer is in a healthy state and apply fixes if needed
   * @returns HealthCheckResult with health status and issues
   */
  checkHealth(): HealthCheckResult {
    const currentState = preciseTimer.getState();
    const healthChecks = this.performHealthChecks(currentState);
    const isHealthy = healthChecks.every(check => check.passed);
    const issues = healthChecks.filter(check => !check.passed).map(check => check.issue);
    
    // Calculate health score
    const passedChecks = healthChecks.filter(check => check.passed).length;
    const healthScore = Math.round((passedChecks / healthChecks.length) * 100);
    
    this.state.healthScore = healthScore;

    if (!isHealthy) {
      this.log('Timer health check failed, applying fixes...', { 
        healthScore, 
        issues: issues.length 
      });
      this.applyFixes();
    } else {
      this.log('Timer health check passed', { healthScore });
    }

    return {
      isHealthy,
      issues,
      score: healthScore
    };
  }

  /**
   * Get current fix state
   * @returns TimerFixState object
   */
  getFixState(): TimerFixState {
    return { ...this.state };
  }

  /**
   * Reset fix state and clear all issues and fixes
   */
  reset(): void {
    this.state = {
      isFixed: false,
      issues: [],
      fixes: [],
      lastCheck: 0,
      healthScore: 100
    };
    this.log('Reset fix state', { action: 'reset' });
  }

  /**
   * Emergency timer reset - complete cleanup and restart
   */
  emergencyReset(): void {
    this.log('Emergency timer reset...', { action: 'emergency_reset' });
    
    try {
      // Stop the timer completely
      preciseTimer.stop();
      
      // Clear debug data
      timerDebugger.clear();
      
      // Reset fix state
      this.reset();
      
      this.log('Emergency reset completed', { action: 'emergency_reset_success' });
    } catch (error) {
      this.log('Emergency reset failed', { error, action: 'emergency_reset_error' });
    }
  }

  // ============================================================================
  // PRIVATE FIX METHODS
  // ============================================================================

  /**
   * Fix state management issues
   */
  private fixStateManagement(): FixResult {
    this.log('Fixing state management...', { fixType: TIMER_FIX_CONSTANTS.FIX_TYPES.STATE_MANAGEMENT });
    
    const currentState = preciseTimer.getState();
    const issuesFixed: string[] = [];
    const fixesApplied: string[] = [];

    // Fix invalid state combinations
    if (currentState.isRunning && currentState.isPaused) {
      this.log('Detected invalid state (running and paused), fixing...', { 
        issue: 'invalid_state_combination' 
      });
      preciseTimer.stop();
      issuesFixed.push('Invalid state: timer was both running and paused');
      fixesApplied.push('Reset timer to clean state');
    }
    
    // Fix time calculation issues
    if (currentState.remainingTime && currentState.remainingTime < 0) {
      this.log('Detected negative remaining time, fixing...', { 
        issue: 'negative_remaining_time' 
      });
      preciseTimer.stop();
      issuesFixed.push('Negative remaining time detected');
      fixesApplied.push('Reset timer due to time calculation error');
    }

    return { success: true, issuesFixed, fixesApplied, timestamp: Date.now() };
  }

  /**
   * Fix pause/resume logic issues
   */
  private fixPauseResumeLogic(): FixResult {
    this.log('Fixing pause/resume logic...', { fixType: TIMER_FIX_CONSTANTS.FIX_TYPES.PAUSE_RESUME });
    
    const currentState = preciseTimer.getState();
    const issuesFixed: string[] = [];
    const fixesApplied: string[] = [];

    // Ensure pause time is properly set
    if (currentState.isPaused && !currentState.pauseTime) {
      this.log('Paused timer missing pause time, fixing...', { 
        issue: 'missing_pause_time' 
      });
      preciseTimer.stop();
      issuesFixed.push('Paused timer missing pause time');
      fixesApplied.push('Reset timer due to pause time corruption');
    }
    
    // Ensure start time is valid
    if (currentState.isRunning && !currentState.startTime) {
      this.log('Running timer missing start time, fixing...', { 
        issue: 'missing_start_time' 
      });
      preciseTimer.stop();
      issuesFixed.push('Running timer missing start time');
      fixesApplied.push('Reset timer due to start time corruption');
    }

    return { success: true, issuesFixed, fixesApplied, timestamp: Date.now() };
  }

  /**
   * Fix interval handling issues
   */
  private fixIntervalHandling(): FixResult {
    this.log('Fixing interval handling...', { fixType: TIMER_FIX_CONSTANTS.FIX_TYPES.INTERVAL_HANDLING });
    
    const currentState = preciseTimer.getState();
    const issuesFixed: string[] = [];
    const fixesApplied: string[] = [];

    // Clear any existing intervals that might be stuck
    if (currentState.isPaused && currentState.isRunning) {
      this.log('Timer is both paused and running, clearing intervals...', { 
        issue: 'inconsistent_state' 
      });
      preciseTimer.stop();
      issuesFixed.push('Timer in inconsistent state (paused and running)');
      fixesApplied.push('Cleared stuck intervals and reset timer');
    }

    return { success: true, issuesFixed, fixesApplied, timestamp: Date.now() };
  }

  /**
   * Fix synchronization issues
   */
  private fixSynchronization(): FixResult {
    this.log('Fixing synchronization...', { fixType: TIMER_FIX_CONSTANTS.FIX_TYPES.SYNCHRONIZATION });
    
    const currentState = preciseTimer.getState();
    const issuesFixed: string[] = [];
    const fixesApplied: string[] = [];

    // Check for time drift
    if (currentState.startTime && currentState.totalDuration) {
      const expectedElapsed = Math.floor((Date.now() - currentState.startTime) / 1000);
      const actualElapsed = currentState.elapsedTime || 0;
      const drift = Math.abs(expectedElapsed - actualElapsed);
      
      if (drift > TIMER_FIX_CONSTANTS.HEALTH.TIME_DRIFT_THRESHOLD) {
        this.log(`Detected time drift of ${drift} seconds, fixing...`, { 
          issue: 'time_drift',
          drift 
        });
        preciseTimer.stop();
        issuesFixed.push(`Time drift detected: ${drift} seconds`);
        fixesApplied.push('Reset timer due to time synchronization drift');
      }
    }

    return { success: true, issuesFixed, fixesApplied, timestamp: Date.now() };
  }

  // ============================================================================
  // HEALTH CHECK METHODS
  // ============================================================================

  /**
   * Perform comprehensive health checks on timer state
   */
  private performHealthChecks(currentState: any): Array<{ passed: boolean; issue?: string }> {
    return [
      // Valid state combinations
      {
        passed: !(currentState.isRunning && currentState.isPaused),
        issue: 'Invalid state: timer is both running and paused'
      },
      // Valid time values
      {
        passed: !currentState.remainingTime || currentState.remainingTime >= 0,
        issue: 'Invalid remaining time: negative value detected'
      },
      // Valid progress
      {
        passed: !currentState.progress || (
          currentState.progress >= TIMER_FIX_CONSTANTS.HEALTH.MIN_PROGRESS && 
          currentState.progress <= TIMER_FIX_CONSTANTS.HEALTH.MAX_PROGRESS
        ),
        issue: 'Invalid progress: value outside valid range (0-100)'
      },
      // Valid session ID if running
      {
        passed: !currentState.isRunning || currentState.sessionId,
        issue: 'Running timer missing session ID'
      },
      // Valid duration if running
      {
        passed: !currentState.isRunning || currentState.totalDuration > 0,
        issue: 'Running timer has invalid duration'
      }
    ];
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Log messages with consistent formatting
   */
  private log(message: string, details?: any): void {
    const emoji = this.getLogEmoji(message);
    const prefix = TIMER_FIX_CONSTANTS.LOGGING.PREFIX;
    
    console.log(`${emoji} ${prefix}: ${message}`, details);
  }

  /**
   * Get appropriate emoji for logging
   */
  private getLogEmoji(message: string): string {
    const emojiMap: Record<string, string> = {
      'Applying comprehensive fixes...': TIMER_FIX_CONSTANTS.LOGGING.EMOJI.APPLY,
      'All fixes applied successfully': TIMER_FIX_CONSTANTS.LOGGING.EMOJI.SUCCESS,
      'Timer health check failed, applying fixes...': TIMER_FIX_CONSTANTS.LOGGING.EMOJI.WARNING,
      'Timer health check passed': TIMER_FIX_CONSTANTS.LOGGING.EMOJI.HEALTH,
      'Reset fix state': TIMER_FIX_CONSTANTS.LOGGING.EMOJI.RESET,
      'Emergency timer reset...': TIMER_FIX_CONSTANTS.LOGGING.EMOJI.EMERGENCY,
      'Emergency reset completed': TIMER_FIX_CONSTANTS.LOGGING.EMOJI.SUCCESS
    };

    return emojiMap[message] || TIMER_FIX_CONSTANTS.LOGGING.EMOJI.APPLY;
  }
}

// ============================================================================
// EXPORTED INSTANCE
// ============================================================================

/**
 * Global timer fix instance
 */
export const timerFix = new TimerFix();

// ============================================================================
// AUTO-APPLY FIXES ON IMPORT
// ============================================================================

/**
 * Automatically apply fixes when the module is imported
 * DISABLED: This was causing initialization errors in production
 */
// timerFix.applyFixes();

// ============================================================================
// GLOBAL EXPOSURE FOR MANUAL DEBUGGING
// ============================================================================

/**
 * Expose timer fix to window for manual debugging in development
 */
if (typeof window !== 'undefined') {
  (window as any).timerFix = timerFix;
  console.log('🔧 TimerFix available as window.timerFix');
}

// ============================================================================
// CONVENIENCE FUNCTIONS
// ============================================================================

/**
 * Quick function to check timer health
 * @returns Health check result
 */
export const checkTimerHealth = (): HealthCheckResult => {
  return timerFix.checkHealth();
};

/**
 * Quick function to apply timer fixes
 * @returns Fix result
 */
export const applyTimerFixes = (): FixResult => {
  return timerFix.applyFixes();
};

/**
 * Quick function to perform emergency reset
 */
export const emergencyTimerReset = (): void => {
  timerFix.emergencyReset();
}; 